import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/api";
import { FISCAL_YEARS } from "@/constants/modules";
import { toast } from "@/lib/toast";
import { Home, Plus, ArrowLeft, Save, Trash2, Calculator, DollarSign, Info, FileText, FileSpreadsheet } from "lucide-react";

const EXPENSE_KEYS = [
  { key: "electricity", label: "Electricite" },
  { key: "heat", label: "Chauffage" },
  { key: "water", label: "Eau" },
  { key: "insurance", label: "Assurance habitation" },
  { key: "property_tax", label: "Taxes foncieres" },
  { key: "rent", label: "Loyer" },
  { key: "maintenance", label: "Entretien / reparations" },
  { key: "internet", label: "Internet" },
  { key: "phone", label: "Telephone" },
];

export default function T777Tab({ year }) {
  const [view, setView] = useState("list");
  const [selectedId, setSelectedId] = useState(null);

  if (view === "form") {
    return <T777Form recordId={selectedId} year={year}
      onBack={() => { setSelectedId(null); setView("list"); }} />;
  }
  return <T777List year={year}
    onSelect={(id) => { setSelectedId(id); setView("form"); }}
    onNew={() => { setSelectedId(null); setView("form"); }} />;
}

function T777List({ year, onSelect, onNew }) {
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.allSettled([
        api.get("/t777", { params: { tax_year: year } }),
        api.get("/clients"),
      ]);
      if (r.status === "fulfilled") setRecords(r.value.data);
      if (c.status === "fulfilled") setClients(c.value.data);
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const getClientName = (cid) => {
    if (!cid) return "Cabinet / generique";
    const c = clients.find(cl => cl.id === cid);
    return c ? `${c.first_name} ${c.last_name}` : "Client";
  };

  // Iter89 — Export server-side via blob download (jamais de mock client-side).
  const exportFile = async (format) => {
    try {
      const params = year ? { tax_year: year } : {};
      const res = await api.get(`/t777/export/${format}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bureau_domicile_T777_${year || "all"}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} telecharge`);
    } catch {
      toast.error("Echec de l'export");
    }
  };

  return (
    <div data-testid="t777-list">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3 text-xs text-blue-800 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>Le formulaire T777 calcule le pourcentage d'espace et de temps utilise pour le travail a domicile, puis applique ce % aux depenses du domicile pour determiner le montant deductible.</span>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-500">{records.length} calcul(s) T777 pour {year}</p>
        <div className="flex gap-2 flex-wrap">
          {/* Iter89 — Exports PDF / Excel (cote serveur) */}
          <button onClick={() => exportFile("pdf")}
            className="border border-rose-300 text-rose-700 hover:bg-rose-50 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5"
            data-testid="t777-export-pdf">
            <FileText className="w-3.5 h-3.5" /> Export PDF
          </button>
          <button onClick={() => exportFile("excel")}
            className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5"
            data-testid="t777-export-excel">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Export Excel
          </button>
          <button onClick={onNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2" data-testid="t777-new-btn">
            <Plus className="w-4 h-4" /> Nouveau calcul
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden divide-y">
        {loading && (
          <div className="px-4 py-10 text-center text-gray-400">
            <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
          </div>
        )}
        {!loading && records.map(r => (
          <button key={r.id} onClick={() => onSelect(r.id)}
            className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-3"
            data-testid={`t777-row-${r.id}`}>
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Home className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{getClientName(r.client_id)} — {r.tax_year}</p>
              <p className="text-xs text-gray-500">
                {r.shared ? "Piece partagee" : "Piece non partagee"} — Bureau : {r.office_sqft} pi² / {r.home_sqft} pi²
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-bold text-green-700">{(r.deductible_amount || 0).toFixed(2)} $</p>
              <p className="text-xs text-gray-400">{(r.combined_percent || 0).toFixed(2)}%</p>
            </div>
          </button>
        ))}
        {!loading && !records.length && (
          <div className="px-4 py-10 text-center text-gray-400">
            <Home className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun calcul T777 sauvegarde pour {year}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function T777Form({ recordId, year, onBack }) {
  const isNew = !recordId;
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    client_id: "", tax_year: year,
    home_sqft: "", office_sqft: "", hours_per_week: "", weeks_per_year: "52",
    shared: true, notes: "",
    electricity: "", heat: "", water: "", insurance: "", property_tax: "",
    rent: "", maintenance: "", internet: "", phone: "",
  });

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/clients"); setClients(r.data); } catch { /* silent */ }
      if (recordId) {
        try {
          const r = await api.get(`/t777/${recordId}`);
          const d = r.data;
          setData({
            client_id: d.client_id || "",
            tax_year: d.tax_year || year,
            home_sqft: d.home_sqft || "",
            office_sqft: d.office_sqft || "",
            hours_per_week: d.hours_per_week || "",
            weeks_per_year: d.weeks_per_year || "52",
            shared: d.shared !== false,
            notes: d.notes || "",
            ...(d.expenses || {}),
          });
        } catch { toast.error("Enregistrement introuvable"); onBack(); }
      }
    })();
  }, [recordId, year, onBack]);

  // Étape 4 — Persistance cumulative : recharge le record T777 existant pour (client, year)
  // quand le client OU l'année change. Permet la saisie progressive au fil de l'année.
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoLoadedId, setAutoLoadedId] = useState(null);
  useEffect(() => {
    if (recordId) return; // mode édition : déjà chargé via recordId
    let cancelled = false;
    (async () => {
      setAutoLoading(true);
      try {
        const params = { tax_year: parseInt(data.tax_year, 10) || year };
        if (data.client_id) params.client_id = data.client_id;
        const r = await api.get("/t777/find", { params });
        if (cancelled) return;
        const d = r.data;
        setAutoLoadedId(d.id);
        setData((prev) => ({
          ...prev,
          home_sqft: d.home_sqft || "",
          office_sqft: d.office_sqft || "",
          hours_per_week: d.hours_per_week || "",
          weeks_per_year: d.weeks_per_year || "52",
          shared: d.shared !== false,
          notes: d.notes || "",
          ...(d.expenses || {}),
        }));
      } catch {
        if (!cancelled) setAutoLoadedId(null);
      } finally {
        if (!cancelled) setAutoLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.client_id, data.tax_year, recordId]);

  // Computed values
  const homeSqft = parseFloat(data.home_sqft) || 0;
  const officeSqft = parseFloat(data.office_sqft) || 0;
  const spacePercent = homeSqft > 0 ? (officeSqft / homeSqft) * 100 : 0;
  const hoursPerWeek = parseFloat(data.hours_per_week) || 0;
  const weeksPerYear = parseFloat(data.weeks_per_year) || 52;
  const totalHoursYear = hoursPerWeek * weeksPerYear;
  const maxHoursYear = 24 * 365;
  const timePercent = maxHoursYear > 0 ? (totalHoursYear / maxHoursYear) * 100 : 0;
  const combinedPercent = data.shared
    ? Math.min((spacePercent / 100) * (timePercent / 100) * 100, 100)
    : Math.min(spacePercent, 100);
  const totalExpenses = useMemo(
    () => EXPENSE_KEYS.reduce((s, e) => s + (parseFloat(data[e.key]) || 0), 0),
    [data],
  );
  const deductibleAmount = totalExpenses * (combinedPercent / 100);

  const validate = () => {
    const e = {};
    if (homeSqft <= 0) e.home_sqft = "Superficie totale requise";
    if (officeSqft <= 0) e.office_sqft = "Superficie bureau requise";
    if (officeSqft > homeSqft && homeSqft > 0) e.office_sqft = "Bureau ne peut depasser la superficie totale";
    if (data.shared && (hoursPerWeek <= 0 || hoursPerWeek > 24 * 7)) e.hours_per_week = "Heures/semaine (1-168)";
    if (data.shared && (weeksPerYear <= 0 || weeksPerYear > 52)) e.weeks_per_year = "Semaines (1-52)";
    return e;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Verifiez les champs"); return; }
    setSaving(true);
    try {
      const expenses = {};
      EXPENSE_KEYS.forEach(e => { expenses[e.key] = parseFloat(data[e.key]) || 0; });
      const payload = {
        client_id: data.client_id || null,
        tax_year: parseInt(data.tax_year),
        shared: data.shared,
        home_sqft: homeSqft, office_sqft: officeSqft,
        hours_per_week: hoursPerWeek, weeks_per_year: weeksPerYear,
        space_percent: spacePercent, combined_percent: combinedPercent,
        expenses, total_expenses: totalExpenses, deductible_amount: deductibleAmount,
        notes: data.notes,
      };
      if (isNew) {
        // Étape 4 — Saisie cumulative : utilise upsert pour 1 record unique par (client, year)
        const r = await api.post("/t777/upsert", payload);
        if (r.data?.created) toast.success("Calcul T777 créé");
        else toast.success("Calcul T777 mis à jour (saisie cumulative)");
      }
      else { await api.put(`/t777/${recordId}`, payload); toast.success("Calcul T777 mis a jour"); }
      onBack();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce calcul T777 ?")) return;
    try { await api.delete(`/t777/${recordId}`); toast.success("Supprime"); onBack(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="t777-form">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3" data-testid="t777-form-back">
        <ArrowLeft className="w-4 h-4" /> Retour aux T777
      </button>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold flex items-center gap-2"><Home className="w-4 h-4" /> {isNew ? "Nouveau calcul T777" : "Modifier le calcul T777"}</h2>
        <div className="flex gap-2">
          {!isNew && (
            <button onClick={handleDelete} className="border border-red-200 text-red-500 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1" data-testid="t777-delete-btn">
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          )}
          <button onClick={handleSave} disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="t777-save-btn">
            <Save className="w-4 h-4" /> {saving ? "..." : isNew ? "Sauvegarder" : "Mettre a jour"}
          </button>
        </div>
      </div>

      {/* Étape 4 — Indicateur de saisie cumulative annuelle */}
      {isNew && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-xs text-emerald-800 flex items-start gap-2" data-testid="t777-cumulative-info">
          {autoLoading ? (
            <span className="w-3 h-3 border border-emerald-300 border-t-emerald-700 rounded-full animate-spin mt-0.5 flex-shrink-0" />
          ) : (
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          )}
          <div>
            <strong>Saisie cumulative annuelle :</strong> les montants sont conservés et accumulés sur l'année.
            {autoLoadedId ? (
              <span> Un calcul existe déjà pour ce client en {data.tax_year} — les données ont été chargées. Modifiez puis sauvegardez pour les mettre à jour.</span>
            ) : (
              <span> Aucun calcul existant pour ce client en {data.tax_year} — la première sauvegarde le créera.</span>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Client (optionnel)</label>
          <select value={data.client_id || ""} onChange={e => setData({ ...data, client_id: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="t777-client-select">
            <option value="">Calcul general (cabinet)</option>
            {clients.map(c => <option key={c.id} value={c.id}>{`${c.first_name} ${c.last_name}`}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Annee fiscale</label>
          <select value={data.tax_year} onChange={e => setData({ ...data, tax_year: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="t777-year-select">
            {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Notes (optionnel)</label>
          <input type="text" value={data.notes || ""} onChange={e => setData({ ...data, notes: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Notes..." data-testid="t777-notes" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Calculator className="w-4 h-4 text-blue-600" /> Superficie et temps</h3>
          <div className="grid grid-cols-2 gap-3">
            <FieldT777 label="Superficie totale (pi²)" error={errors.home_sqft}>
              <input type="number" value={data.home_sqft} onChange={e => setData({ ...data, home_sqft: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.home_sqft ? "border-red-300 bg-red-50" : ""}`}
                placeholder="1200" data-testid="t777-home-sqft" />
            </FieldT777>
            <FieldT777 label="Bureau (pi²)" error={errors.office_sqft}>
              <input type="number" value={data.office_sqft} onChange={e => setData({ ...data, office_sqft: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.office_sqft ? "border-red-300 bg-red-50" : ""}`}
                placeholder="150" data-testid="t777-office-sqft" />
            </FieldT777>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-800">Pourcentage superficie : <span className="font-bold">{spacePercent.toFixed(1)}%</span></p>
          </div>

          <div className="border rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-gray-600 mb-2">Type d'espace de bureau :</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={data.shared} onChange={() => setData({ ...data, shared: true })} data-testid="t777-shared-yes" />
              <span className="text-sm">Piece partagee (usage mixte personnel/travail)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={!data.shared} onChange={() => setData({ ...data, shared: false })} data-testid="t777-shared-no" />
              <span className="text-sm">Piece non partagee (usage exclusif travail)</span>
            </label>
          </div>

          {data.shared ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <FieldT777 label="Heures / semaine" error={errors.hours_per_week}>
                  <input type="number" value={data.hours_per_week} onChange={e => setData({ ...data, hours_per_week: e.target.value })}
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.hours_per_week ? "border-red-300 bg-red-50" : ""}`}
                    placeholder="40" data-testid="t777-hours" />
                </FieldT777>
                <FieldT777 label="Semaines / annee" error={errors.weeks_per_year}>
                  <input type="number" value={data.weeks_per_year} onChange={e => setData({ ...data, weeks_per_year: e.target.value })}
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.weeks_per_year ? "border-red-300 bg-red-50" : ""}`}
                    placeholder="52" data-testid="t777-weeks" />
                </FieldT777>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 space-y-1">
                <p className="text-sm text-blue-800">Heures annuelles : <span className="font-bold">{totalHoursYear.toFixed(0)}</span> / {maxHoursYear}</p>
                <p className="text-sm text-blue-800">Pourcentage temps : <span className="font-bold">{timePercent.toFixed(1)}%</span></p>
              </div>
            </>
          ) : (
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-sm text-green-800">Piece non partagee : <span className="font-bold">100 %</span> du pourcentage superficie</p>
            </div>
          )}

          <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
            <p className="text-sm font-semibold text-indigo-800">Pourcentage applicable : <span className="text-lg font-bold">{combinedPercent.toFixed(2)}%</span></p>
            <p className="text-xs text-indigo-600 mt-1">
              {data.shared
                ? `Espace (${spacePercent.toFixed(1)}%) × Temps (${timePercent.toFixed(1)}%)`
                : `Espace (${spacePercent.toFixed(1)}%) × 100%`}
            </p>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold flex items-center gap-2"><DollarSign className="w-4 h-4 text-red-600" /> Depenses annuelles du domicile</h3>
          <div className="space-y-2">
            {EXPENSE_KEYS.map(exp => (
              <div key={exp.key} className="flex items-center gap-2">
                <label className="text-sm text-gray-600 w-44 flex-shrink-0">{exp.label}</label>
                <div className="relative flex-1">
                  <input type="number" step="0.01" value={data[exp.key]} onChange={e => setData({ ...data, [exp.key]: e.target.value })}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm text-right" placeholder="0.00"
                    data-testid={`t777-${exp.key}`} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">$</span>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-gray-600">Total depenses</span><span className="font-medium tabular-nums">{totalExpenses.toFixed(2)} $</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-600">Pourcentage applicable</span><span className="font-medium">{combinedPercent.toFixed(2)}%</span></div>
            <div className="flex justify-between font-bold text-base bg-green-50 rounded-lg p-3 border border-green-200">
              <span className="text-green-800">Montant deductible T777</span>
              <span className="text-green-700 tabular-nums" data-testid="t777-deductible">{deductibleAmount.toFixed(2)} $</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldT777({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}
